import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import useAnchoredPosition from '../../hooks/useAnchoredPosition';

const CustomDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  displayProperty = 'label',
  valueProperty = 'value',
  searchEnabled = false,
  className = '',
  style = {},
  disabled = false,
  label = '',
  labelClassName = '',
  dropdownClassName = '',
  optionClassName = '',
  searchPlaceholder = "Search...",
  noOptionsText = "No options found",
  paddingY ="py-2.5",
  // Optional custom chevron node (V2 feed passes the design-doc glyph);
  // default keeps the classic 20px chevron below.
  chevronIcon = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);

  // D-003 — the menu renders in a body portal, anchored to the trigger, so the
  // toolbar/modal `overflow-x-auto` ancestors can never clip it (A3).
  const { panelRef, style: menuStyle } = useAnchoredPosition(isOpen, triggerRef, {
    align: 'left',
    gap: 4,
    matchTriggerWidth: true,
  });

  // Update filtered options when options or searchTerm changes
  useEffect(() => {
    if (!searchEnabled || !searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option =>
        option[displayProperty]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [options, searchTerm, searchEnabled, displayProperty]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // The menu lives in a body portal, so a click on it is NOT inside
      // dropdownRef — check the portalled panel too, or selecting an option
      // would close the menu on mousedown before the click lands.
      const inTrigger = dropdownRef.current && dropdownRef.current.contains(event.target);
      const inPanel = panelRef.current && panelRef.current.contains(event.target);
      if (!inTrigger && !inPanel) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [panelRef]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchEnabled && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchEnabled]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  const handleOptionClick = (option) => {
    onChange(option[valueProperty]);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Get display text and image for selected value
  const getDisplayText = () => {
    if (!value) return placeholder;
    const selectedOption = options.find(option => option[valueProperty] === value);
    return selectedOption ? selectedOption[displayProperty] : placeholder;
  };

  // Get selected option for image display
  const getSelectedOption = () => {
    if (!value) return null;
    return options.find(option => option[valueProperty] === value);
  };

  // Prevent dropdown from closing when clicking on search input
  const handleSearchClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className={`block text-[16px] font-medium text-[#000000] mb-2 ${labelClassName}`}>
          {label}
        </label>
      )}
      
      {/* Dropdown Trigger */}
      <div
        ref={triggerRef}
        className={`w-full px-4  bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium cursor-pointer flex items-center justify-between ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        } ${className} ${paddingY}`}
        style={{ 
          color: value ? '#000000' : '#00000099',
          ...style 
        }}
        onClick={handleToggle}
      >
        <div className="flex items-center gap-2 truncate flex-1">
          {getSelectedOption()?.iconClassName ? (
            <span
              className={`${getSelectedOption().iconClassName} flex-shrink-0`}
              style={{ width: '1.333em', height: '1em' }}
              role="img"
              aria-label={getDisplayText()}
            />
          ) : getSelectedOption()?.image && (
            <img
              src={getSelectedOption().image}
              alt={getDisplayText()}
              className="w-5 h-4 object-cover rounded flex-shrink-0"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
          <span className="truncate">{getDisplayText()}</span>
        </div>
        {chevronIcon ? (
          <span
            className={`flex-shrink-0 transition-transform duration-200 ${
              isOpen ? 'transform rotate-180' : ''
            }`}
          >
            {chevronIcon}
          </span>
        ) : (
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${
              isOpen ? 'transform rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </div>

      {/* Dropdown Menu — rendered in a body portal (D-003) so no ancestor
          overflow clips it; positioned via useAnchoredPosition. */}
      {isOpen && createPortal(
        <div
          ref={panelRef}
          style={menuStyle || { position: 'fixed', top: 0, left: 0, visibility: 'hidden' }}
          className={`z-[9999] bg-white border border-[#E9E9E9] rounded-lg shadow-lg max-h-60 overflow-hidden ${dropdownClassName}`}
        >
          {/* Search Input */}
          {searchEnabled && (
            <div className="p-2 border-b border-[#E9E9E9]">
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onClick={handleSearchClick}
                placeholder={searchPlaceholder}
                className="w-full px-3 py-2 text-sm border border-[#E9E9E9] rounded focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder:text-[#898D95]"
              />
            </div>
          )}

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {noOptionsText}
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <div
                  key={option[valueProperty] || index}
                  className={`px-4 py-3 text-[16px] font-medium cursor-pointer hover:bg-gray-50 transition-colors duration-150 flex items-center gap-2 ${
                    value === option[valueProperty] ? 'bg-blue-50 text-blue-600' : 'text-[#000000]'
                  } ${optionClassName}`}
                  onClick={() => handleOptionClick(option)}
                >
                  {option.iconClassName ? (
                    <span
                      className={`${option.iconClassName} flex-shrink-0`}
                      style={{ width: '1.333em', height: '1em' }}
                      role="img"
                      aria-label={option[displayProperty]}
                    />
                  ) : option.image && (
                    <img
                      src={option.image}
                      alt={option[displayProperty]}
                      className="w-5 h-4 object-cover rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <span>{option[displayProperty]}</span>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default CustomDropdown;
